import {Component, EventEmitter, Input, Output, OnChanges, SimpleChanges} from '@angular/core';
import {AttendeeService, PointExplanation} from "../../services/attendee.service";
import {MatDialog} from "@angular/material/dialog";
import {firstValueFrom} from "rxjs";
import {
  MakeVolunteerTaskAdjustmentComponent
} from "../../dialogs/adjustments/make-volunteer-task-adjustment/make-volunteer-task-adjustment.component";
import {
  MakeClassTaskAdjustmentComponent
} from "../../dialogs/adjustments/make-class-task-adjustment/make-class-task-adjustment.component";
import {
  MakeQuizAdjustmentComponent
} from "../../dialogs/adjustments/make-quiz-adjustment/make-quiz-adjustment.component";
import { MatTooltipModule } from '@angular/material/tooltip';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { NgIf, NgFor } from '@angular/common';
import { HudComponent } from "../hud/hud.component";

@Component({
    selector: 'app-points-breakdown',
    templateUrl: './points-breakdown.component.html',
    styleUrls: ['./points-breakdown.component.scss'],
    standalone: true,
    imports: [NgIf, NgFor, FaIconComponent, MatTooltipModule, HudComponent]
})
export class PointsBreakdownComponent implements OnChanges {


  @Input()
  roomId?: string ;

  @Input()
  userId?: string ;

  @Input()
  canAdjust: boolean = false ;

  @Output()
  adjustmentMade: EventEmitter<undefined> = new EventEmitter<undefined>();

  explanations: PointExplanation[] = [];


  pointsEarned: number  = 0 ;

  participationPointsOffered: number = 0 ;
  participationPointsEarned: number = 0 ;

  knowledgePointsOffered: number = 0 ;
  knowledgePointsEarned: number = 0 ;

  constructor (
    private $attendees: AttendeeService,
    private $dialog: MatDialog,
  ) {
  }



  getScoreClass(score: number): string {

    if (score> 0) {
      return 'text-positive' ;
    } else if (score < 0) {
      return 'text-negative' ;
    } else {
      return 'text-muted text-sm' ;
    }
  }

  getRatioClass(earned: number, offered: number): string {

    if (earned == 0 && offered == 0) {
      return 'text-muted text-sm' ;
    }

    if (earned == offered) {
      return 'text-positive'
    } else {
      return 'text-negative'
    }
  }

  getFormattedScore(score: number): string {

    if (score > 0) {
      return "+" + score ;
    }
    return "" + score ;
  }


  async ngOnChanges(changes: SimpleChanges) {
    await this.load();
  }

  async load() {

    if (!this.roomId){
      return ;
    }

    this.explanations = await firstValueFrom(this.$attendees.getPointExplanations(this.roomId, this.userId)) ;

    this.pointsEarned = 0 ;
    this.participationPointsOffered = 0 ;
    this.participationPointsEarned = 0 ;

    this.knowledgePointsOffered = 0 ;
    this.knowledgePointsEarned = 0 ;

    if (this.explanations) {

      this.explanations.forEach( (explanation) => {

        this.pointsEarned += explanation.pointsEarned ;

        this.participationPointsOffered += explanation.participationPointsOffered ;
        this.participationPointsEarned += explanation.participationPointsEarned ;

        this.knowledgePointsOffered += explanation.knowledgePointsOffered ;
        this.knowledgePointsEarned += explanation.knowledgePointsEarned ;
      })
    }
  }


  public async makeAdjustment(e: PointExplanation) {

    switch (e.type) {
      case "VOLUNTEER_TASK":
        await this.makeVolunteerTaskAdjustment(e) ;
        break ;
      case "CLASS_TASK":
        await this.makeClassTaskAdjustment(e);
        break;
      case "QUIZ":
        await this.makeQuizAdjustment(e);
        break ;
    }
  }

  private async makeVolunteerTaskAdjustment(e: PointExplanation) {

    const dialogRef = this.$dialog.open(
      MakeVolunteerTaskAdjustmentComponent,
      {
        panelClass: 'dialog',
        data: {
          taskId: e.taskId,
          userId: this.userId,
          decision: (e.optedIn) ? 'OPT_IN' : 'OPT_OUT',
          assessment: e.assessment,
          explanation: e.adjustmentExplanation
        }
      });

    const result = await firstValueFrom(dialogRef.afterClosed()) ;
    if (result) {
      this.adjustmentMade.emit();
      await this.load();
    }

  }

  private async makeClassTaskAdjustment(e: PointExplanation) {

    const dialogRef = this.$dialog.open(
      MakeClassTaskAdjustmentComponent,
      {
        panelClass: 'dialog',
        data: {
          taskId: e.taskId,
          userId: this.userId,
          subtaskIndex: e.subtaskIndex,
          completed: e.completed,
          assessment: e.assessment,
          explanation: e.adjustmentExplanation
        }
      });

    const result = await firstValueFrom(dialogRef.afterClosed()) ;
    if (result) {
      this.adjustmentMade.emit();
      await this.load();
    }

  }

  private async makeQuizAdjustment(e: PointExplanation) {

    const dialogRef = this.$dialog.open(
      MakeQuizAdjustmentComponent,
      {
        panelClass: 'dialog',
        data: {
          quizId: e.quizId,
          userId: this.userId,
          questionIndex: e.questionIndex,
          answered: e.answered,
          correct: e.correct,
          explanation: e.adjustmentExplanation
        }
      });

    const result = await firstValueFrom(dialogRef.afterClosed()) ;
    if (result) {
      this.adjustmentMade.emit();
      await this.load();
    }

  }


}
